import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Title, usePermissions, useNotify, Button } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ReTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import { API_URL } from '../config';
import { JumpstartFilterText, JumpstartFilterSelect, JumpstartButton } from '../components/JumpstartInputs';
import { useT } from '../i18n/useT';
import type { AppTranslationKey } from '../i18n/types';

// ─── types ────────────────────────────────────────────────────────────────────

interface StatusInfo {
    labelKey: AppTranslationKey;
    color: 'default' | 'primary' | 'success' | 'error';
    icon: React.ReactNode;
}

interface DailyEntry {
    date: string;
    total: number;
    completed: number;
    errors: number;
}

interface Stats {
    total: number;
    today: number;
    this_week: number;
    this_month: number;
    today_completed: number;
    today_errors: number;
    status_distribution: { pending: number; processing: number; completed: number; error: number };
    processing_type_distribution: Record<string, number>;
    document_type_distribution: Record<string, number>;
    daily_data: DailyEntry[];
}

interface QueueTask {
    queue_id: string;
    user_id: number;
    user_email: string;
    user_full_name: string | null;
    processing_type: string;
    document_type: string;
    sku: string | null;
    consumer_email: string | null;
    status: number;
    progress: number;
    error_code: string | null;
    error_message: string | null;
    backchannel_url: string | null;
    created_at: string | null;
    started_at: string | null;
    completed_at: string | null;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const STATUS_MAP: Record<number, StatusInfo> = {
    0: { labelKey: 'resources.queue.statuses.pending',    color: 'default',  icon: <PendingIcon fontSize="small" /> },
    1: { labelKey: 'resources.queue.statuses.processing', color: 'primary',  icon: <HourglassEmptyIcon fontSize="small" /> },
    2: { labelKey: 'resources.queue.statuses.completed',  color: 'success',  icon: <CheckCircleIcon fontSize="small" /> },
    3: { labelKey: 'resources.queue.statuses.error',      color: 'error',    icon: <ErrorIcon fontSize="small" /> },
};

const PIE_COLORS = ['#9e9e9e', '#2196f3', '#4caf50', '#f44336'];

const authHeaders = (): Record<string, string> => ({
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});

const getDownloadFilename = (contentDisposition: string | null, fallback: string): string => {
    if (!contentDisposition) {
        return fallback;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch {
            // Fall through to basic filename parsing.
        }
    }

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1] || fallback;
};

const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : '—';

// ─── Stat box ─────────────────────────────────────────────────────────────────

const StatBox = ({ label, value, color }: { label: string; value: number; color?: string }) => (
    <Card variant="outlined" sx={{ textAlign: 'center', p: 1.5, flex: '1 1 120px' }}>
        <Typography variant="h4" color={color || 'text.primary'} fontWeight={700}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Card>
);

// ─── Stats + charts ───────────────────────────────────────────────────────────

const MonitoringStats = ({ onRefresh }: { onRefresh: number }) => {
    const t = useT();
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        fetch(`${API_URL}/queue/admin/stats`, { headers: authHeaders() })
            .then(r => r.json())
            .then((d: Stats) => setStats(d))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [onRefresh]);

    if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>;
    if (!stats?.status_distribution) return null;

    const statusPie = [
        { name: t('resources.queue.statuses.pending'),    value: stats.status_distribution.pending },
        { name: t('resources.queue.statuses.processing'), value: stats.status_distribution.processing },
        { name: t('resources.queue.statuses.completed'),  value: stats.status_distribution.completed },
        { name: t('resources.queue.statuses.error'),      value: stats.status_distribution.error },
    ].filter(d => d.value > 0);

    return (
        <Box mb={3}>
            {/* Summary boxes */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
                <StatBox label={t('monitoring.stats.total')}        value={stats.total} />
                <StatBox label={t('monitoring.stats.today')}        value={stats.today} />
                <StatBox label={t('monitoring.stats.thisWeek')}     value={stats.this_week} />
                <StatBox label={t('monitoring.stats.thisMonth')}    value={stats.this_month} />
                <StatBox label={t('monitoring.stats.doneToday')}    value={stats.today_completed} color="#4caf50" />
                <StatBox label={t('monitoring.stats.errorsToday')}  value={stats.today_errors}    color="#f44336" />
            </Box>

            {/* Charts row — Box flex replaces MUI Grid to stay compatible with MUI v7 */}
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
                {/* Daily bar chart */}
                <Card variant="outlined" sx={{ flex: '2 1 400px' }}>
                    <CardContent>
                        <Typography variant="subtitle2" gutterBottom>{t('monitoring.charts.daily')}</Typography>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={stats.daily_data} margin={{ top: 4, right: 8, bottom: 4, left: -10 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }}
                                    tickFormatter={(d: string) => d.slice(5)} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                <ReTooltip />
                                <Legend />
                                <Bar dataKey="total"     name={t('monitoring.charts.dailyTotal')}     fill="#2196f3" />
                                <Bar dataKey="completed" name={t('monitoring.charts.dailyCompleted')} fill="#4caf50" />
                                <Bar dataKey="errors"    name={t('monitoring.charts.dailyErrors')}    fill="#f44336" />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Status pie */}
                <Card variant="outlined" sx={{ flex: '1 1 240px' }}>
                    <CardContent>
                        <Typography variant="subtitle2" gutterBottom>{t('monitoring.charts.statusDistribution')}</Typography>
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={statusPie} dataKey="value" nameKey="name"
                                    cx="50%" cy="50%" outerRadius={80}
                                    label={({ name, value }: { name: string; value: number }) => `${name}: ${value}`}>
                                    {statusPie.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <ReTooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

// ─── Detail dialog ────────────────────────────────────────────────────────────

const DetailRow = ({ label, children, error }: { label: string; children: React.ReactNode; error?: boolean }) => (
    <Box display="flex" gap={1} py={0.5} alignItems="flex-start">
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ minWidth: 140, flexShrink: 0 }}>
            {label}
        </Typography>
        <Box>{children}</Box>
    </Box>
);

const DetailDialog = ({
    task,
    open,
    onClose,
    onDownload,
}: {
    task: QueueTask | null;
    open: boolean;
    onClose: () => void;
    onDownload: (queueId: string) => void;
}) => {
    const t = useT();
    if (!task) return null;
    const si = STATUS_MAP[task.status] ?? STATUS_MAP[0];
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{t('monitoring.detail.title')}</span>
                <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <DetailRow label={t('monitoring.detail.queueId')}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {task.queue_id}
                    </Typography>
                </DetailRow>
                <DetailRow label={t('monitoring.detail.status')}>
                    <Chip label={t(si.labelKey)} color={si.color} size="small" icon={si.icon as React.ReactElement} />
                </DetailRow>
                <DetailRow label={t('monitoring.detail.progress')}>
                    <Box display="flex" alignItems="center" gap={1} width={200}>
                        <LinearProgress variant="determinate" value={task.progress || 0}
                            color={task.status === 3 ? 'error' : 'primary'} sx={{ flex: 1 }} />
                        <Typography variant="caption">{task.progress || 0}%</Typography>
                    </Box>
                </DetailRow>

                <Divider sx={{ my: 1 }} />

                <DetailRow label={t('monitoring.detail.operator')}>
                    <Typography variant="body2">{task.user_full_name || task.user_email || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{task.user_email}</Typography>
                </DetailRow>
                <DetailRow label={t('monitoring.detail.sku')}>
                    <Typography variant="body2">{task.sku || '—'}</Typography>
                </DetailRow>
                <DetailRow label={t('monitoring.detail.consumerEmail')}>
                    <Typography variant="body2">{task.consumer_email || '—'}</Typography>
                </DetailRow>
                <DetailRow label={t('monitoring.detail.processingType')}>
                    <Typography variant="body2">{task.processing_type}</Typography>
                </DetailRow>
                <DetailRow label={t('monitoring.detail.documentType')}>
                    <Typography variant="body2">{task.document_type}</Typography>
                </DetailRow>

                <Divider sx={{ my: 1 }} />

                <DetailRow label={t('monitoring.detail.createdAt')}>
                    <Typography variant="body2">{fmt(task.created_at)}</Typography>
                </DetailRow>
                <DetailRow label={t('monitoring.detail.startedAt')}>
                    <Typography variant="body2">{fmt(task.started_at)}</Typography>
                </DetailRow>
                <DetailRow label={t('monitoring.detail.completedAt')}>
                    <Typography variant="body2">{fmt(task.completed_at)}</Typography>
                </DetailRow>

                {task.error_code && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <DetailRow label={t('monitoring.detail.errorCode')} error>
                            <Typography variant="body2" color="error">{task.error_code}</Typography>
                        </DetailRow>
                        <DetailRow label={t('monitoring.detail.errorMessage')} error>
                            <Typography variant="body2" color="error">{task.error_message || '—'}</Typography>
                        </DetailRow>
                    </>
                )}

                {task.status === 2 && (
                    <Box mt={2}>
                        <Button
                            label={t('monitoring.detail.downloadProcessed')}
                            variant="contained"
                            startIcon={<CloudDownloadIcon />}
                            onClick={() => onDownload(task.queue_id)}
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

// ─── Filters panel (memoized) ──────────────────────────────────────────────────
// Isolated and memoized so live SSE refreshes of the table data do not re-render
// the panel, which would otherwise close an open native <select> dropdown.

interface MonitoringFiltersProps {
    isAdmin: boolean;
    filterSku: string;
    setFilterSku: (v: string) => void;
    filterEmail: string;
    setFilterEmail: (v: string) => void;
    filterStatus: string;
    setFilterStatus: (v: string) => void;
    filterProcessingType: string;
    setFilterProcessingType: (v: string) => void;
    filterDocumentType: string;
    setFilterDocumentType: (v: string) => void;
    onApply: () => void;
    onClear: () => void;
}

const MonitoringFilters = React.memo((props: MonitoringFiltersProps) => {
    const {
        isAdmin,
        filterSku, setFilterSku,
        filterEmail, setFilterEmail,
        filterStatus, setFilterStatus,
        filterProcessingType, setFilterProcessingType,
        filterDocumentType, setFilterDocumentType,
        onApply, onClear,
    } = props;
    const t = useT();

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="subtitle2" gutterBottom>{t('monitoring.filters.title')}</Typography>
                <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
                    <JumpstartFilterText
                        label={t('monitoring.filters.sku')}
                        value={filterSku}
                        onChange={setFilterSku}
                        onEnter={onApply}
                        minWidth={130}
                    />
                    {isAdmin && (
                        <JumpstartFilterText
                            label={t('monitoring.filters.consumerEmail')}
                            value={filterEmail}
                            onChange={setFilterEmail}
                            onEnter={onApply}
                            minWidth={200}
                        />
                    )}
                    <JumpstartFilterSelect
                        label={t('monitoring.filters.status')}
                        value={filterStatus}
                        onChange={setFilterStatus}
                        minWidth={130}
                        choices={[
                            { id: '',  name: t('actions.all') },
                            { id: '0', name: t('resources.queue.statuses.pending') },
                            { id: '1', name: t('resources.queue.statuses.processing') },
                            { id: '2', name: t('resources.queue.statuses.completed') },
                            { id: '3', name: t('resources.queue.statuses.error') },
                        ]}
                    />
                    <JumpstartFilterSelect
                        label={t('monitoring.filters.processingType')}
                        value={filterProcessingType}
                        onChange={setFilterProcessingType}
                        minWidth={160}
                        choices={[
                            { id: '',          name: t('actions.all') },
                            { id: 'watermark', name: t('monitoring.processingTypes.watermark') },
                            { id: 'sign',      name: t('monitoring.processingTypes.sign') },
                        ]}
                    />
                    <JumpstartFilterSelect
                        label={t('monitoring.filters.documentType')}
                        value={filterDocumentType}
                        onChange={setFilterDocumentType}
                        minWidth={140}
                        choices={[
                            { id: '',     name: t('actions.all') },
                            { id: 'pdf',  name: t('monitoring.documentTypes.pdf') },
                            { id: 'epub', name: t('monitoring.documentTypes.epub') },
                        ]}
                    />
                    <JumpstartButton label={t('actions.apply')} type="primary"   onClick={onApply} />
                    <JumpstartButton label={t('actions.clear')} type="secondary" onClick={onClear} />
                </Box>
            </CardContent>
        </Card>
    );
});

// ─── Main monitoring list ──────────────────────────────────────────────────────

export const MonitoringList = () => {
    const { permissions } = usePermissions();
    const notify = useNotify();
    const t = useT();
    const eventSourceRef = useRef<EventSource | null>(null);
    const sseErrorShownRef = useRef(false);

    const [refreshKey, setRefreshKey] = useState(0);

    const [filterSku,            setFilterSku]            = useState('');
    const [filterEmail,          setFilterEmail]          = useState('');
    const [filterStatus,         setFilterStatus]         = useState('');
    const [filterProcessingType, setFilterProcessingType] = useState('');
    const [filterDocumentType,   setFilterDocumentType]   = useState('');

    const [page,        setPage]        = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortBy,      setSortBy]      = useState('created_at');
    const [sortOrder,   setSortOrder]   = useState<'asc' | 'desc'>('desc');

    const [rows,    setRows]    = useState<QueueTask[]>([]);
    const [total,   setTotal]   = useState(0);
    const [loading, setLoading] = useState(false);

    const [selected, setSelected] = useState<QueueTask | null>(null);

    const isAdmin  = permissions === 'admin';
    const endpoint = isAdmin ? `${API_URL}/queue/admin/tasks` : `${API_URL}/queue/my-tasks`;

    const fetchData = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({
            skip:       String(page * rowsPerPage),
            limit:      String(rowsPerPage),
            sort_by:    sortBy,
            sort_order: sortOrder,
        });
        if (filterSku)            params.set('sku',             filterSku);
        if (filterEmail)          params.set('consumer_email',  filterEmail);
        if (filterStatus !== '')  params.set('status',          filterStatus);
        if (filterProcessingType) params.set('processing_type', filterProcessingType);
        if (filterDocumentType)   params.set('document_type',   filterDocumentType);

        fetch(`${endpoint}?${params}`, { headers: authHeaders() })
            .then(async r => {
                const data: QueueTask[] = await r.json();
                const tot = parseInt(r.headers.get('X-Total-Count') || String(data.length), 10);
                setRows(Array.isArray(data) ? data : []);
                setTotal(tot);
            })
            .catch(() => notify(t('monitoring.notifications.loadFailed'), { type: 'error' }))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, page, rowsPerPage, sortBy, sortOrder,
        filterSku, filterEmail, filterStatus, filterProcessingType, filterDocumentType, refreshKey]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            return;
        }

        const url = `${API_URL}/queue/stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        eventSourceRef.current = es;
        sseErrorShownRef.current = false;

        es.onmessage = event => {
            try {
                const data = JSON.parse(event.data) as { type?: string };
                if (data.type === 'refresh') {
                    setRefreshKey(key => key + 1);
                }
            } catch {
                // Ignore malformed SSE messages.
            }
        };

        es.onerror = () => {
            if (!sseErrorShownRef.current) {
                notify(t('monitoring.notifications.sseInterrupted'), { type: 'warning' });
                sseErrorShownRef.current = true;
            }
            es.close();
            eventSourceRef.current = null;
        };

        return () => {
            es.close();
            eventSourceRef.current = null;
        };
    }, [notify, t]);

    const handleSort = (field: string) => {
        if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
        else { setSortBy(field); setSortOrder('desc'); }
        setPage(0);
    };

    const applyFilters = useCallback(() => { setPage(0); setRefreshKey(k => k + 1); }, []);
    const clearFilters = useCallback(() => {
        setFilterSku(''); setFilterEmail(''); setFilterStatus('');
        setFilterProcessingType(''); setFilterDocumentType('');
        setPage(0); setRefreshKey(k => k + 1);
    }, []);

    const handleDownload = async (queueId: string) => {
        try {
            const r = await fetch(`${API_URL}/queue/${queueId}/download`, { headers: authHeaders() });
            if (!r.ok) throw new Error('failed');
            const cd = r.headers.get('Content-Disposition');
            const filename = getDownloadFilename(cd, `document_${queueId}`);
            const blob = await r.blob();
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            URL.revokeObjectURL(url); document.body.removeChild(a);
            notify(t('monitoring.notifications.downloadSuccess'), { type: 'success' });
        } catch {
            notify(t('monitoring.notifications.downloadFailed'), { type: 'error' });
        }
    };

    const SortTh = ({ field, label }: { field: string; label: string }) => (
        <TableSortLabel
            active={sortBy === field}
            direction={sortBy === field ? sortOrder : 'asc'}
            onClick={() => handleSort(field)}
        >{label}</TableSortLabel>
    );

    return (
        <Box p={2}>
            <Title title={t('monitoring.title')} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={600}>{t('monitoring.title')}</Typography>
                <Tooltip title={t('monitoring.tooltips.refresh')}>
                    <IconButton onClick={() => setRefreshKey(k => k + 1)} color="primary">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {isAdmin && <MonitoringStats onRefresh={refreshKey} />}

            {/* Filters */}
            <MonitoringFilters
                isAdmin={isAdmin}
                filterSku={filterSku}
                setFilterSku={setFilterSku}
                filterEmail={filterEmail}
                setFilterEmail={setFilterEmail}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                filterProcessingType={filterProcessingType}
                setFilterProcessingType={setFilterProcessingType}
                filterDocumentType={filterDocumentType}
                setFilterDocumentType={setFilterDocumentType}
                onApply={applyFilters}
                onClear={clearFilters}
            />

            {/* Table */}
            <Paper variant="outlined">
                {loading && <LinearProgress />}
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell><SortTh field="queue_id"        label={t('monitoring.table.queueId')} /></TableCell>
                                {isAdmin && <TableCell>{t('monitoring.table.operator')}</TableCell>}
                                <TableCell><SortTh field="sku"             label={t('monitoring.table.sku')} /></TableCell>
                                <TableCell>{t('monitoring.table.consumerEmail')}</TableCell>
                                <TableCell><SortTh field="processing_type" label={t('monitoring.table.type')} /></TableCell>
                                <TableCell><SortTh field="document_type"   label={t('monitoring.table.doc')} /></TableCell>
                                <TableCell><SortTh field="status"          label={t('monitoring.table.status')} /></TableCell>
                                <TableCell>{t('monitoring.table.progress')}</TableCell>
                                <TableCell><SortTh field="created_at"      label={t('monitoring.table.created')} /></TableCell>
                                <TableCell><SortTh field="completed_at"    label={t('monitoring.table.completed')} /></TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={isAdmin ? 11 : 10} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">{t('monitoring.table.empty')}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map(row => {
                                const si      = STATUS_MAP[row.status] ?? STATUS_MAP[0];
                                const isError = row.status === 3;
                                return (
                                    <TableRow
                                        key={row.queue_id}
                                        hover
                                        onClick={() => setSelected(row)}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor:    isError ? 'rgba(244,67,54,0.06)' : undefined,
                                            '&:hover': { bgcolor: isError ? 'rgba(244,67,54,0.12)' : undefined },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                {row.queue_id.slice(0, 8)}…
                                            </Typography>
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell>
                                                <Typography variant="body2">{row.user_full_name || row.user_email || '—'}</Typography>
                                                <Typography variant="caption" color="text.secondary">{row.user_email}</Typography>
                                            </TableCell>
                                        )}
                                        <TableCell>{row.sku || '—'}</TableCell>
                                        <TableCell>{row.consumer_email || '—'}</TableCell>
                                        <TableCell>{row.processing_type}</TableCell>
                                        <TableCell>{row.document_type}</TableCell>
                                        <TableCell>
                                            <Chip label={t(si.labelKey)} color={si.color} size="small"
                                                icon={si.icon as React.ReactElement} />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 110 }}>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <LinearProgress variant="determinate" value={row.progress || 0}
                                                    color={isError ? 'error' : 'primary'} sx={{ flex: 1 }} />
                                                <Typography variant="caption">{row.progress || 0}%</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">{fmt(row.created_at)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">{fmt(row.completed_at)}</Typography>
                                        </TableCell>
                                        <TableCell onClick={e => e.stopPropagation()}>
                                            {row.status === 2 && (
                                                <Tooltip title={t('monitoring.tooltips.download')}>
                                                    <IconButton size="small"
                                                        onClick={() => handleDownload(row.queue_id)}>
                                                        <CloudDownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Paper>

            <DetailDialog
                task={selected}
                open={!!selected}
                onClose={() => setSelected(null)}
                onDownload={handleDownload}
            />
        </Box>
    );
};

export const monitoringIcon = MonitorHeartIcon;
